/**
 * Canonical Pino logger module keys used across the backend codebase.
 *
 * Each leaf value is the exact string passed as the `module` binding when creating a child logger,
 * for example: `RootLogger.child({ module: PinoModules.api.controllers.zoneController })`.
 *
 * The keys are organized into a nested object to reflect the module hierarchy and to make it easy
 * to discover the full logger structure when adding a new logger.
 *
 * Enforced at compile time: `RootLogger.child(...)` requires `module` to satisfy
 * {@link PinoModuleString}, the union of leaf values in this object.
 */
export const PinoModules = {
    api: {
        authorization: "api:authorization",
        middleware: {
            catchAllError: "api:middleware:catchAllError",
            requestValidationError: "api:middleware:requestValidationError",
        },
        controllers: {
            aoiController: "api:controllers:AoiController",
            assetIdentityControllerV2: "api:controllers:AssetIdentityControllerV2",
            openApiController: "api:controllers:OpenApiController",
            dataPacketController: "api:controllers:DataPacketController",
            dataStreamCsvExportControllerV2: "api:controllers:DataStreamCsvExportControllerV2",
            deviceController: "api:controllers:DeviceController",
            deviceRegistration: "api:controllers:DeviceRegistration",
            gen3Logs: "api:controllers:gen3:logs",
            gen4Logs: "api:controllers:gen4:logs",
            geolocationController: "api:controllers:GeolocationController",
            imageController: "api:controllers:ImageController",
            ownerRecentDataController: "api:controllers:OwnerRecentDataController",
            productionJigReportController: "api:controllers:ProductionJigReportController",
            sensorTerraController: "api:controllers:SensorTerraController",
            specialCaseController: "api:controllers:SpecialCaseController",
            thirdPartyDeviceController: "api:controllers:ThirdPartyDeviceController",
            userController: "api:controllers:UserController",
            userControllerV2: "api:controllers:UserControllerV2",
            zoneController: "api:controllers:ZoneController",
        },
        openApiRegistry: "api:openApiRegistry",
        organizationController: "api:organization-controller",
        root: "api",
        routes: "api:routes",
        userProfileController: "api:user-profile-controller",
    },
    collectionStats: {
        totalDocumentCount: "collectionStats:totalDocumentCount",
    },
    common: {
        externalServices: {
            descope: "common:externalServices:descope",
            ePlant: "common:externalServices:ePlant",
            tti: "common:externalServices:tti",
            twilio: "externalServices:twilio",
            weatherLink: "common:externalServices:weatherLink",
        },
    },
    dashboardAPI: {
        utils: {
            responder: "dashboardAPI:utils:responder",
        },
    },
    dataStreamService: "data-stream-service",
    communication: {
        bestEffortDeviceSave: "communication:bestEffortDeviceSave",
        draginoECSensorReceiver: "communication:DraginoECSensorReceiver",
        draginoSDI12BLSReceiver: "communication:DraginoSDI12BLSReceiver",
        ePlantUplinkReceiver: "communication:ePlantUplinkReceiver",
        generalHandlers: "communication:generalHandlers",
        parsedUplinkUtils: "communication:parsedUplinkUtils",
    },
    database: {
        postgres: {
            testSetup: "database:postgres:test-setup",
        },
        redis: {
            testSetup: "database:redis:test-setup",
        },
        s3: {
            testSetup: "database:s3:test-setup",
        },
        timeSeries: {
            configuration: "database:time-series:configuration",
        },
    },
    deviceCommunication: {
        clearQueuedDownlinks: "deviceCommunication:clearQueuedDownlinks",
        diagnostics: "deviceCommunication:diagnostics",
        offlineDeviceService: "deviceCommunication:offlineDeviceService",
        parsedUplink: "deviceCommunication:parsedUplink",
    },
    gen3: {
        communication: {
            getParsedUplink: "gen3:communication:GetParsedUplink",
            parsedUplinkProto: "gen3:communication:ParsedUplinkProto",
            sproutHeartbeatUplink: "gen3:communication:SproutHeartbeatUplink",
            sproutLogger: "gen3:communication:SproutLogger",
            sproutPacketInfo: "gen3:communication:SproutPacketInfo",
            sproutSchedule: "gen3:communication:SproutSchedule",
            sproutSensorUplink: "gen3:communication:SproutSensorUplink",
            sproutUplinkParser: "gen3:communication:SproutUplinkParser",
            sproutUplinkReceiver: "gen3:communication:SproutUplinkReceiver",
        },
    },
    gen4: {
        communication: {
            downlinkAcknowledgement: "gen4:communication:downlinkAcknowledgement",
            gen4ParsedUplink: "gen4:communication:Gen4ParsedUplink",
            gen4PromptedDownlink: "gen4:communication:Gen4PromptedDownlink",
            gen4PromptedDownlinkDecision: "gen4:communication:Gen4PromptedDownlink:Decision",
            gen4UplinkReceiver: "gen4:communication:Gen4UplinkReceiver",
            getLoggerId: "gen4:communication:getLoggerId",
            initializeLoggerFromRawUplink: "gen4:communication:initializeLoggerFromRawUplink",
            loadGen4Models: "gen4:communication:loadGen4Models",
            logDownlinkToSend: "gen4:communication:logDownlinkToSend",
            saplingLogger: "gen4:communication:SaplingLogger",
            saplingSchedule: "gen4:communication3:SaplingSchedule",
            sendDownlink: "gen4:communication:sendDownlink",
            uplinkDecoderBase: "gen4:communication:uplinkDecoderBase",
            validation: "gen4:communication:validation",
        },
        deviceCommunication: {
            saplingJoinReqHandler: "gen4:deviceCommunication:SaplingJoinReqHandler",
            saplingJoinReqLogger: "gen4:deviceCommunication:SaplingJoinReqLogger",
        },
        verification: {
            populateWarningsForSensorThresholds: "gen4:populateWarningsForSensorThresholds",
            updateSproutVerification: "gen4:verification:UpdateSproutVerification",
        },
    },
    inboundMessage: {
        accept: "inboundMessage:accept",
        commandWorker: "inboundMessage:commandWorker",
        complianceWorker: "inboundMessage:complianceWorker",
        intakeWorker: "inboundMessage:intakeWorker",
        webhook: "inboundMessage:webhook",
    },
    jobs: {
        diagnostics: "jobs:diagnostics",
    },
    models: {
        assetIdentity: "models:AssetIdentity",
        dataPacket: "models:DataPacket",
        generateWarningSummary: "models:generateWarningSummary",
        historicalDeviceConfiguration: "models:HistoricalDeviceConfiguration",
        sendWarningNotification: "models:sendWarningNotification",
        sensoterraDevice: "models:SensoterraDevice",
        smsSpamProtection: "models:SmsSpamProtection",
        thirdPartyDeviceSyncHook: "models:ThirdPartyDeviceSyncHook",
        user: "models:User",
    },
    mongo: "mongo",
    notificationPipeline: {
        actions: "notificationPipeline:actions",
        consent: "notificationPipeline:consent",
        composeWorker: "notificationPipeline:composeWorker",
        handleWarningEvent: "notificationPipeline:handleWarningEvent",
        scheduleCompose: "notificationPipeline:scheduleCompose",
    },
    outboundMessage: {
        delivery: "outboundMessage:delivery",
        queue: "outboundMessage:queue",
        worker: "outboundMessage:worker",
    },
    smsWelcomeMessage: {
        actions: "smsWelcomeMessage:actions",
        queue: "smsWelcomeMessage:queue",
        worker: "smsWelcomeMessage:worker",
    },
    weatherLinkIngest: {
        hooks: "weatherLinkIngest:hooks",
        queue: "weatherLinkIngest:queue",
        worker: "weatherLinkIngest:worker",
        /** The settings-page device sync (SWD-3371): discovery, onboarding, pause, soft delete. */
        deviceSync: "weatherLinkIngest:deviceSync",
        /** The history walker that drains bounded cursors: import backfills and gap repairs. */
        backfill: "weatherLinkIngest:backfill",
    },
    periodicScripts: {
        checkScheduleTracker: "periodicScripts:CheckScheduleTracker",
        collectSensoterraData: "periodicScripts:CollectSensoterraData",
        collectWeatherData: "periodicScripts:CollectWeatherData",
        diagnostics: "periodicScripts:diagnostics",
        doOWPProtection: "periodicScripts:DoOWPProtection",
        logBackup: "periodicScripts:LogBackup",
        periodicMaintenance: "periodicScripts:PeriodicMaintenance",
        periodicMaintenanceTasks: "periodicScripts:PeriodicMaintenanceTasks",
        processHistory: "periodicScripts:ProcessHistory",
        reconcileGeneratedSensorClusters: "periodicScripts:ReconcileGeneratedSensorClusters",
        refreshSensingPeriods: "periodicScripts:RefreshSensingPeriods",
        runRoutineOnAllDevices: "periodicScripts:RunRoutineOnAllDevices",
        sendSMSAlerts: "periodicScripts:SendSMSAlerts",
        sproutLogBackup: "periodicScripts:SproutLogBackup",
        syncOrganizationNormalizedNames: "periodicScripts:SyncOrganizationNormalizedNames",
        transferLogs: "periodicScripts:TransferLogs",
        updateAOIcontents: "periodicScripts:UpdateAOIcontents",
        updateRepeatingCommandCachedIndexes: "periodicScripts:UpdateRepeatingCommandCachedIndexes",
        updateResponseTimeStats: "periodicScripts:UpdateResponseTimeStats",
        updateSproutPacketLoss: "periodicScripts:UpdateSproutPacketLoss",
    },
    caching: "caching",
    cachingDiagnostics: "caching:diagnostics",
    redis: "redis",
    shutdown: "shutdown",
    s3: {
        utilities: "s3:utilities",
    },
    scheduling: {
        irrigationActiveDates: "scheduling:IrrigationActiveDates",
        irrigationController: "scheduling:IrrigationController",
        irrigationSetController: "scheduling:IrrigationSetController",
        trackIntendedSchedule: "scheduling:TrackIntendedSchedule",
        verifyDevicesInZone: "scheduling:VerifyDevicesInZone",
        zoneIntendedStateTracking: "scheduling:ZoneIntendedStateTracking",
    },
    services: {
        dataStreamService: "services:DataStreamService",
        generatedSensorClusterReconciliation: "services:GeneratedSensorClusterReconciliation",
        sensorFragmentService: "services:SensorFragmentService",
        sensoterraAuth: "services:SensoterraAuth",
        sensoterraUtilities: "services:SensoterraUtilities",
    },
    testing: {
        globalSetup: "testing:global-setup",
        simulatedRedis: "testing:simulated-redis",
        useFullSimulatedServer: "testing:useFullSimulatedServer",
    },
    warnings: {
        manualOverrideWarningHandler: "warnings:manualOverrideWarningHandler",
        warningOperations: "warnings:warningOperations",
    },
    webhook: {
        slack: "webhook:slack",
    },
};
//# sourceMappingURL=pinoModules.js.map